import { z } from 'zod'
import { moneySchema, optionalText, pageQuerySchema, uuidSchema, versionSchema } from './common.js'
import { compareMoney } from './money.js'
import { type ModuleKey, type Permission, permissionModule } from './permissions.js'
import { TIMER_UNITS, type TimerUnit } from './working-calendar.js'

/**
 * Configurable approval workflows (ADR-018, docs/refactor/WORKFLOWS.md).
 *
 * Configuration: a workflow definition belongs to one subject (a document type that registered for
 * approval) and has immutable published versions plus at most one draft. A version's `spec` holds the
 * applicability conditions, the sequential approval steps and the approved outcome.
 * Execution: submitting a document starts a run pinned to the version that matched; each step
 * activates tasks for the approvers resolved at that moment; decisions are revalidated when made.
 * Domain: the only effects on the document are the module's own commands (e.g. post a purchase).
 */

// ── subjects: the document types that can be routed for approval ────────────────

export const WORKFLOW_SUBJECTS = [
  'purchase',
  'project_budget',
  'project_variation',
  'project_claim',
  'service_contract',
] as const
export type WorkflowSubject = (typeof WORKFLOW_SUBJECTS)[number]
export const workflowSubjectSchema = z.enum(WORKFLOW_SUBJECTS)

/**
 * money: an OMR amount (3 decimals); number: any decimal (counts, days, percentages); date: a calendar
 * date `YYYY-MM-DD`; text: free text compared without letter case; enum: one of the values the subject
 * declares; ref / refSet: an id / a set of ids.
 */
export type WorkflowFieldType = 'money' | 'number' | 'date' | 'text' | 'enum' | 'ref' | 'refSet'
/** What a reference field points at, so the builder can offer a picker. */
export type WorkflowRefKind = 'supplier' | 'supplierGroup' | 'warehouse' | 'role' | 'orgUnit' | 'projectType'

/**
 * The module a ref kind's records belong to: the builder offers the kind's picker only while that module
 * is on. Null: core data, always available (all of them in R0a; org units are core, ADR-025).
 */
export const WORKFLOW_REF_KIND_MODULE: Record<WorkflowRefKind, ModuleKey | null> = {
  supplier: null,
  supplierGroup: null,
  warehouse: null,
  role: null,
  orgUnit: null,
  projectType: 'projects',
}

/** A value an enum field may take: lowercase (condition values are lowercased), with labels. */
export interface WorkflowEnumValue {
  value: string
  en: string
  ar: string
}

export interface WorkflowFieldDef {
  type: WorkflowFieldType
  ref?: WorkflowRefKind
  /** enum fields: the values the subject declares. */
  values?: readonly WorkflowEnumValue[]
  en: string
  ar: string
  /** A plain-language note shown in the builder (which amount, tax, currency). */
  hintEn?: string
  hintAr?: string
  /**
   * Hidden (null) from viewers who may not read the document, like money fields: a figure derived from
   * amounts (a budget's margin %).
   */
  sensitive?: boolean
}

export type WorkflowOperator =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'in'
  | 'notIn'
  | 'empty'
  | 'notEmpty'
  | 'within'
  | 'notWithin'

/**
 * Operators each field type accepts. `refSet` in/notIn mean "any of" / "none of". Org unit references
 * also accept `within` / `notWithin` (see `fieldOperators`).
 */
export const WORKFLOW_OPERATORS: Record<WorkflowFieldType, readonly WorkflowOperator[]> = {
  money: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'empty', 'notEmpty'],
  date: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'empty', 'notEmpty'],
  text: ['eq', 'neq', 'in', 'notIn', 'empty', 'notEmpty'],
  enum: ['eq', 'neq', 'in', 'notIn', 'empty', 'notEmpty'],
  ref: ['eq', 'neq', 'in', 'notIn', 'empty', 'notEmpty'],
  refSet: ['in', 'notIn', 'empty', 'notEmpty'],
}

export const WORKFLOW_OPERATOR_LABELS: Record<WorkflowOperator, { en: string; ar: string }> = {
  eq: { en: 'is', ar: 'يساوي' },
  neq: { en: 'is not', ar: 'لا يساوي' },
  gt: { en: 'is more than', ar: 'أكبر من' },
  gte: { en: 'is at least', ar: 'أكبر من أو يساوي' },
  lt: { en: 'is less than', ar: 'أقل من' },
  lte: { en: 'is at most', ar: 'أقل من أو يساوي' },
  in: { en: 'is any of', ar: 'أي من' },
  notIn: { en: 'is none of', ar: 'ليس أيًا من' },
  empty: { en: 'is empty', ar: 'فارغ' },
  notEmpty: { en: 'is not empty', ar: 'غير فارغ' },
  within: { en: 'is within', ar: 'ضمن' },
  notWithin: { en: 'is not within', ar: 'ليس ضمن' },
}

/** Subtree operators: the unit or any unit below it. Only org unit references take them. */
export const WORKFLOW_SUBTREE_OPERATORS: readonly WorkflowOperator[] = ['within', 'notWithin']

/** The derived fact holding an org unit field's unit and its ancestors (unit first), set by the server. */
export function orgPathFact(field: string): string {
  return `${field}.path`
}

/** The operators a field accepts: its type's, plus the subtree operators for an org unit reference. */
export function fieldOperators(def: Pick<WorkflowFieldDef, 'type' | 'ref'>): readonly WorkflowOperator[] {
  const base = WORKFLOW_OPERATORS[def.type]
  return def.type === 'ref' && def.ref === 'orgUnit' ? [...base, ...WORKFLOW_SUBTREE_OPERATORS] : base
}

/** Date fields read their comparisons in time: "is after", "is before". */
export const WORKFLOW_DATE_OPERATOR_LABELS: Partial<Record<WorkflowOperator, { en: string; ar: string }>> = {
  eq: { en: 'is on', ar: 'في' },
  neq: { en: 'is not on', ar: 'ليس في' },
  gt: { en: 'is after', ar: 'بعد' },
  gte: { en: 'is on or after', ar: 'في أو بعد' },
  lt: { en: 'is before', ar: 'قبل' },
  lte: { en: 'is on or before', ar: 'في أو قبل' },
}

/** The label of an operator for a field of this type (dates read "is after" / "is before"). */
export function operatorLabel(
  operator: WorkflowOperator,
  type?: WorkflowFieldType,
): { en: string; ar: string } {
  const date = type === 'date' ? WORKFLOW_DATE_OPERATOR_LABELS[operator] : undefined
  return date ?? WORKFLOW_OPERATOR_LABELS[operator]
}

export interface WorkflowActionDef {
  en: string
  ar: string
  /** The publisher must hold this permission to configure the action (no escalation through the builder). */
  permission: Permission
}

export interface WorkflowSubjectDef {
  en: string
  ar: string
  /** Who may read the document: an approver must hold it (record access). */
  readPermission: Permission
  /** The business permission a person needs to decide an approval of this document type. */
  decidePermission: Permission
  /** Who may submit a document for approval (the maker). */
  submitPermission: Permission
  /** Fields a condition may test: a restricted, typed catalogue. */
  fields: Record<string, WorkflowFieldDef>
  /** Actions the approved outcome may run: the module's own commands. */
  actions: Record<string, WorkflowActionDef>
  /** The money field shown as the request's amount in inboxes and run lists; default `total`. */
  amountField?: string
}

export const WORKFLOW_SUBJECT_DEFS: Record<WorkflowSubject, WorkflowSubjectDef> = {
  purchase: {
    en: 'Purchase',
    ar: 'مشتريات',
    readPermission: 'purchasing.read',
    decidePermission: 'purchases.approve',
    submitPermission: 'purchases.create',
    fields: {
      total: {
        type: 'money',
        en: 'Total including VAT',
        ar: 'الإجمالي شامل الضريبة',
        hintEn: 'The purchase total after discounts, VAT included, in OMR (3 decimals).',
        hintAr: 'إجمالي المشتريات بعد الخصم وشامل ضريبة القيمة المضافة، بالريال العماني (3 منازل).',
      },
      subtotal: {
        type: 'money',
        en: 'Subtotal before VAT',
        ar: 'المجموع قبل الضريبة',
        hintEn: 'After discounts, VAT excluded, in OMR (3 decimals).',
        hintAr: 'بعد الخصم ودون ضريبة القيمة المضافة، بالريال العماني (3 منازل).',
      },
      supplier: { type: 'ref', ref: 'supplier', en: 'Supplier', ar: 'المورد' },
      supplierGroup: {
        type: 'refSet',
        ref: 'supplierGroup',
        en: 'Supplier group',
        ar: 'مجموعة المورد',
        hintEn: 'The groups the supplier belongs to when the purchase is submitted.',
        hintAr: 'المجموعات التي ينتمي إليها المورد عند تقديم المشتريات.',
      },
      warehouse: { type: 'ref', ref: 'warehouse', en: 'Receiving warehouse', ar: 'مستودع الاستلام' },
      orgUnit: {
        type: 'ref',
        ref: 'orgUnit',
        en: 'Requesting unit',
        ar: 'الوحدة الطالبة',
        hintEn: 'The org unit the purchase is for; "is within" also matches the units below it.',
        hintAr: 'الوحدة التنظيمية التي تخصها المشتريات؛ "ضمن" تشمل أيضًا الوحدات التابعة لها.',
      },
      requesterRole: {
        type: 'refSet',
        ref: 'role',
        en: 'Requester’s role',
        ar: 'دور مقدم الطلب',
        hintEn: 'The roles of the person who submits the purchase.',
        hintAr: 'أدوار الشخص الذي يقدم المشتريات.',
      },
    },
    actions: {
      post: {
        en: 'Post the purchase (receive the goods and record the bill)',
        ar: 'ترحيل المشتريات (استلام البضاعة وتسجيل الفاتورة)',
        permission: 'purchases.post',
      },
    },
  },
  /** A project budget version (projects/02 §7.4–7.5); applies while the project's budget approval is on. */
  project_budget: {
    en: 'Project budget',
    ar: 'موازنة المشروع',
    readPermission: 'projects.financials',
    decidePermission: 'projects.budgets_approve',
    submitPermission: 'projects.budgets',
    amountField: 'totalCost',
    fields: {
      totalCost: {
        type: 'money',
        en: 'Budgeted cost',
        ar: 'التكلفة المقدرة',
        hintEn: 'The sum of the budget lines, in OMR (3 decimals).',
        hintAr: 'مجموع بنود الموازنة، بالريال العماني (3 منازل).',
      },
      revenue: {
        type: 'money',
        en: 'Budgeted revenue',
        ar: 'الإيراد المقدر',
        hintEn: 'The revenue the budget plans against; empty for internal projects.',
        hintAr: 'الإيراد الذي تُبنى عليه الموازنة؛ فارغ للمشاريع الداخلية.',
      },
      changeVsBaseline: {
        type: 'money',
        en: 'Change against the approved budget',
        ar: 'التغيير عن الموازنة المعتمدة',
        hintEn:
          'Budgeted cost minus the approved version’s (the whole cost for a first budget); negative when lower.',
        hintAr:
          'التكلفة المقدرة ناقص تكلفة النسخة المعتمدة (كامل التكلفة للموازنة الأولى)؛ سالب عند الانخفاض.',
      },
      marginPct: {
        type: 'number',
        sensitive: true,
        en: 'Budgeted margin %',
        ar: 'نسبة الهامش المقدر',
        hintEn: '(Revenue − cost) ÷ revenue × 100; empty without revenue.',
        hintAr: '(الإيراد − التكلفة) ÷ الإيراد × 100؛ فارغ دون إيراد.',
      },
      orgUnit: {
        type: 'ref',
        ref: 'orgUnit',
        en: 'Project’s unit',
        ar: 'وحدة المشروع',
        hintEn: 'The org unit the project belongs to; "is within" also matches the units below it.',
        hintAr: 'الوحدة التنظيمية التي يتبعها المشروع؛ "ضمن" تشمل أيضًا الوحدات التابعة لها.',
      },
      projectType: { type: 'ref', ref: 'projectType', en: 'Project type', ar: 'نوع المشروع' },
      requesterRole: {
        type: 'refSet',
        ref: 'role',
        en: 'Requester’s role',
        ar: 'دور مقدم الطلب',
        hintEn: 'The roles of the person who submits the budget.',
        hintAr: 'أدوار الشخص الذي يقدم الموازنة.',
      },
    },
    actions: {
      approve: {
        en: 'Approve the budget (it becomes the project’s baseline)',
        ar: 'اعتماد الموازنة (تصبح الموازنة الأساسية للمشروع)',
        permission: 'projects.budgets_approve',
      },
    },
  },
  /** A variation before it goes to the client (projects/04 §15.1, P5); the final approval submits it. */
  project_variation: {
    en: 'Project variation',
    ar: 'أمر تغييري',
    readPermission: 'projects.contract',
    decidePermission: 'projects.contract_approve',
    submitPermission: 'projects.contract',
    amountField: 'value',
    fields: {
      value: {
        type: 'money',
        en: 'Variation value',
        ar: 'قيمة الأمر التغييري',
        hintEn: 'The sum of its lines, ex-VAT; negative for omissions.',
        hintAr: 'مجموع بنوده قبل الضريبة؛ سالب عند الحذف.',
      },
      timeImpactDays: { type: 'number', en: 'Time impact (days)', ar: 'أثر المدة (أيام)' },
      kind: {
        type: 'enum',
        en: 'Kind',
        ar: 'النوع',
        values: [
          { value: 'client_instruction', en: 'Client instruction', ar: 'تعليمات العميل' },
          { value: 'design_change', en: 'Design change', ar: 'تغيير في التصميم' },
          { value: 'site_condition', en: 'Site condition', ar: 'ظروف الموقع' },
          { value: 'ps_expenditure', en: 'Provisional sum', ar: 'مبلغ احتياطي' },
          { value: 'omission', en: 'Omission', ar: 'حذف' },
        ],
      },
      orgUnit: { type: 'ref', ref: 'orgUnit', en: 'Project’s unit', ar: 'وحدة المشروع' },
      projectType: { type: 'ref', ref: 'projectType', en: 'Project type', ar: 'نوع المشروع' },
      requesterRole: { type: 'refSet', ref: 'role', en: 'Requester’s role', ar: 'دور مقدم الطلب' },
    },
    actions: {
      submit: {
        en: 'Submit the variation to the client',
        ar: 'تقديم الأمر التغييري إلى العميل',
        permission: 'projects.contract',
      },
    },
  },
  /** A progress claim before it goes to the client (projects/04 §15.1, P5); the final approval submits it. */
  project_claim: {
    en: 'Progress claim',
    ar: 'مستخلص',
    readPermission: 'projects.contract',
    decidePermission: 'projects.contract_approve',
    submitPermission: 'projects.contract',
    amountField: 'net',
    fields: {
      net: {
        type: 'money',
        en: 'Net applied for',
        ar: 'صافي المطالبة',
        hintEn: 'This period, after retention, advance recovery and delay damages, ex-VAT.',
        hintAr: 'لهذه الفترة بعد المحتجزات واسترداد الدفعة المقدمة وغرامات التأخير، قبل الضريبة.',
      },
      grossThisPeriod: { type: 'money', en: 'Gross this period', ar: 'إجمالي الفترة' },
      kind: {
        type: 'enum',
        en: 'Kind',
        ar: 'النوع',
        values: [
          { value: 'interim', en: 'Interim', ar: 'مرحلي' },
          { value: 'final', en: 'Final', ar: 'ختامي' },
        ],
      },
      orgUnit: { type: 'ref', ref: 'orgUnit', en: 'Project’s unit', ar: 'وحدة المشروع' },
      projectType: { type: 'ref', ref: 'projectType', en: 'Project type', ar: 'نوع المشروع' },
      requesterRole: { type: 'refSet', ref: 'role', en: 'Requester’s role', ar: 'دور مقدم الطلب' },
    },
    actions: {
      submit: {
        en: 'Submit the claim to the client',
        ar: 'تقديم المستخلص إلى العميل',
        permission: 'projects.contract',
      },
    },
  },
  /**
   * A maintenance contract before it starts (Service C4, C2_C4_BREAKDOWN decision 38): Activate submits it
   * while a published workflow applies; the final approval activates it.
   */
  service_contract: {
    en: 'Maintenance contract',
    ar: 'عقد صيانة',
    readPermission: 'contracts.read',
    decidePermission: 'contracts.approve',
    submitPermission: 'contracts.manage',
    amountField: 'value',
    fields: {
      value: {
        type: 'money',
        en: 'Contract value',
        ar: 'قيمة العقد',
        hintEn: 'For the whole period, VAT excluded, in OMR (3 decimals).',
        hintAr: 'للفترة كاملة دون ضريبة القيمة المضافة، بالريال العماني (3 منازل).',
      },
      durationMonths: {
        type: 'number',
        en: 'Duration (months)',
        ar: 'المدة (بالأشهر)',
        hintEn: 'From the start to the end date, in whole and part months (e.g. 12, 6.5).',
        hintAr: 'من تاريخ البدء إلى تاريخ الانتهاء بالأشهر الكاملة والجزئية (مثل 12 أو 6.5).',
      },
      siteCount: { type: 'number', en: 'Sites', ar: 'عدد المواقع' },
      coveredAssets: {
        type: 'number',
        en: 'Covered assets',
        ar: 'المعدات المشمولة',
        hintEn: 'Assets the contract covers when it is submitted.',
        hintAr: 'المعدات التي يشملها العقد عند تقديمه.',
      },
      coverage: {
        type: 'enum',
        en: 'Coverage',
        ar: 'التغطية',
        values: [
          { value: 'all', en: 'Everything at the sites', ar: 'كل المعدات في المواقع' },
          { value: 'types', en: 'Chosen asset types', ar: 'أنواع معدات محددة' },
          { value: 'assets', en: 'Picked assets', ar: 'معدات مختارة' },
        ],
      },
      parts: {
        type: 'enum',
        en: 'Parts',
        ar: 'قطع الغيار',
        values: [
          { value: 'included', en: 'Included', ar: 'مشمولة' },
          { value: 'excluded', en: 'Billed', ar: 'بمقابل' },
          { value: 'capped', en: 'Included up to a cap', ar: 'مشمولة حتى سقف' },
        ],
      },
      renewal: {
        type: 'enum',
        en: 'New or renewal',
        ar: 'جديد أو تجديد',
        values: [
          { value: 'new', en: 'New contract', ar: 'عقد جديد' },
          { value: 'renewal', en: 'Renewal', ar: 'تجديد' },
        ],
      },
      requesterRole: { type: 'refSet', ref: 'role', en: 'Requester’s role', ar: 'دور مقدم الطلب' },
    },
    actions: {
      activate: {
        en: 'Activate the contract (it starts covering jobs and its plans make visits)',
        ar: 'تفعيل العقد (يبدأ تغطية الأوامر وتُنشئ خططه الزيارات)',
        permission: 'contracts.manage',
      },
    },
  },
}

/**
 * The module that owns a subject's documents (purchases: `purchasing`). While it is off, the subject's
 * requests are on hold like when workflows are off (ADR-024 D3): nothing is decided, retried or posted.
 */
export function subjectModule(subject: WorkflowSubject): ModuleKey {
  const module = permissionModule(WORKFLOW_SUBJECT_DEFS[subject].readPermission)
  if (!module) throw new Error(`Workflow subject "${subject}" belongs to no module`)
  return module
}

/**
 * A field or action of a subject's catalogue by name: own keys only, so a name such as "constructor"
 * or "__proto__" from a request is unknown rather than an inherited property.
 */
export function subjectField(subject: WorkflowSubject, key: string): WorkflowFieldDef | undefined {
  const fields = WORKFLOW_SUBJECT_DEFS[subject].fields
  return Object.hasOwn(fields, key) ? fields[key] : undefined
}

export function subjectAction(subject: WorkflowSubject, key: string): WorkflowActionDef | undefined {
  const actions = WORKFLOW_SUBJECT_DEFS[subject].actions
  return Object.hasOwn(actions, key) ? actions[key] : undefined
}

/** Field name → type, for typed condition evaluation. */
export type WorkflowFieldTypes = Readonly<Record<string, WorkflowFieldType>>

/** The type of each field of a subject's catalogue, for `evaluateCondition`. */
export function fieldTypesOf(subject: WorkflowSubject): WorkflowFieldTypes {
  return Object.fromEntries(
    Object.entries(WORKFLOW_SUBJECT_DEFS[subject].fields).map(([k, d]) => [k, d.type]),
  )
}

// ── conditions (typed, no expressions) ───────────────────────────────────────────

export interface WorkflowRule {
  kind: 'rule'
  field: string
  operator: WorkflowOperator
  /** money: a decimal string; ref: an id; in/notIn: ids; empty/notEmpty: none. */
  value?: string | string[] | null
}
export interface WorkflowGroup {
  kind: 'group'
  match: 'all' | 'any'
  /** NOT: the group holds when its children do not. */
  negate: boolean
  children: WorkflowCondition[]
}
export type WorkflowCondition = WorkflowRule | WorkflowGroup

export const WORKFLOW_CONDITION_MAX_DEPTH = 3
export const WORKFLOW_CONDITION_MAX_CHILDREN = 20

const ruleSchema = z
  .object({
    kind: z.literal('rule'),
    field: z.string().min(1).max(40),
    operator: z.enum([
      'eq',
      'neq',
      'gt',
      'gte',
      'lt',
      'lte',
      'in',
      'notIn',
      'empty',
      'notEmpty',
      'within',
      'notWithin',
    ]),
    // Ids compare as text against facts from the database, which are lowercase: an id typed in
    // uppercase must not silently fail to match. Amounts have no letters, so they are unaffected.
    value: z
      .union([z.string().max(40), z.array(z.string().max(40)).max(100)])
      .nullish()
      .transform((v) => (typeof v === 'string' ? v.toLowerCase() : v?.map((s) => s.toLowerCase()))),
  })
  .strict()

/**
 * How deep parsing follows groups: past WORKFLOW_CONDITION_MAX_DEPTH, so `conditionProblems` can explain
 * the limit, but bounded, so a pathologically nested body is refused as invalid instead of exhausting
 * the stack. At the last level a group may only hold rules.
 */
const CONDITION_PARSE_DEPTH = 10

function conditionSchemaAt(depth: number): z.ZodType<WorkflowCondition> {
  const child: z.ZodType<WorkflowCondition> =
    depth < CONDITION_PARSE_DEPTH ? z.lazy(() => conditionSchemaAt(depth + 1)) : ruleSchema
  return z.union([
    ruleSchema,
    z
      .object({
        kind: z.literal('group'),
        match: z.enum(['all', 'any']),
        negate: z.boolean().default(false),
        children: z.array(child).max(WORKFLOW_CONDITION_MAX_CHILDREN),
      })
      .strict(),
  ]) as z.ZodType<WorkflowCondition>
}

export const workflowConditionSchema: z.ZodType<WorkflowCondition> = conditionSchemaAt(1)

/** Problems with a condition for its subject: unknown fields, wrong operators, mistyped values. */
export function conditionProblems(
  subject: WorkflowSubject,
  condition: WorkflowCondition,
  path = 'conditions',
  depth = 1,
): { path: string; message: string }[] {
  if (condition.kind === 'group') {
    if (depth > WORKFLOW_CONDITION_MAX_DEPTH) {
      return [
        { path, message: `Conditions can be nested at most ${WORKFLOW_CONDITION_MAX_DEPTH} levels deep` },
      ]
    }
    return condition.children.flatMap((c, i) =>
      conditionProblems(subject, c, `${path}.children.${i}`, depth + 1),
    )
  }
  const def = subjectField(subject, condition.field)
  if (!def) return [{ path: `${path}.field`, message: 'Unknown field' }]
  if (!fieldOperators(def).includes(condition.operator)) {
    return [{ path: `${path}.operator`, message: 'This comparison does not apply to the field' }]
  }
  const v = condition.value
  const uuid = (s: unknown) => typeof s === 'string' && uuidSchema.safeParse(s).success
  const declared = (s: unknown) => typeof s === 'string' && (def.values ?? []).some((e) => e.value === s)
  const text = (s: unknown) => typeof s === 'string' && s.trim().length > 0
  const member = def.type === 'enum' ? declared : def.type === 'text' ? text : uuid
  switch (condition.operator) {
    case 'empty':
    case 'notEmpty':
      return v == null || (Array.isArray(v) && v.length === 0)
        ? []
        : [{ path: `${path}.value`, message: 'No value is needed' }]
    case 'in':
    case 'notIn':
      return Array.isArray(v) && v.length > 0 && v.every(member)
        ? []
        : [{ path: `${path}.value`, message: 'Choose at least one value' }]
    default:
      switch (def.type) {
        case 'money':
          return typeof v === 'string' && moneySchema.safeParse(v).success
            ? []
            : [{ path: `${path}.value`, message: 'Enter an amount with at most 3 decimals' }]
        case 'number':
          return typeof v === 'string' && NUMBER_TEXT.test(v)
            ? []
            : [{ path: `${path}.value`, message: 'Enter a number' }]
        case 'date':
          return isDateText(v) ? [] : [{ path: `${path}.value`, message: 'Enter a date (YYYY-MM-DD)' }]
        case 'text':
          return text(v) ? [] : [{ path: `${path}.value`, message: 'Enter a value' }]
        case 'enum':
          return declared(v) ? [] : [{ path: `${path}.value`, message: 'Choose one of the listed values' }]
        default:
          return uuid(v) ? [] : [{ path: `${path}.value`, message: 'Choose a value' }]
      }
  }
}

/** A decimal number (number fields): up to 15 integer digits and 6 decimals. */
const NUMBER_TEXT = /^-?\d{1,15}(\.\d{1,6})?$/
const DATE_TEXT = /^(\d{4})-(\d{2})-(\d{2})$/

/** A real calendar date written `YYYY-MM-DD` (no 2026-02-30). */
function isDateText(v: unknown): v is string {
  if (typeof v !== 'string') return false
  const m = DATE_TEXT.exec(v)
  if (!m) return false
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return d.toISOString().slice(0, 10) === v
}

/**
 * The facts a subject exposes to conditions, captured when the document is submitted: money as
 * decimal strings, references as ids (null when absent), sets as id arrays.
 */
export type WorkflowFacts = Record<string, string | string[] | null>

/** One evaluated rule or group, for explanations ("why did this match"). */
export interface ConditionTrace {
  kind: 'rule' | 'group'
  path: string
  passed: boolean
  field?: string
  operator?: WorkflowOperator
  value?: string | string[] | null
  actual?: string | string[] | null
  match?: 'all' | 'any'
  negate?: boolean
}

/**
 * Evaluates a validated condition against facts: exact decimal comparison for money (no floats), id
 * equality for references. A missing fact never satisfies a comparison (only `empty`). The same
 * function runs for real submissions and for the simulator.
 *
 * `types` (from `fieldTypesOf`) makes each comparison follow the field's declared type: numbers and
 * money as exact decimals, dates in calendar order, text without letter case, enums and ids as values.
 * Without it, a value that looks like an amount compares as one and anything else as text.
 */
export function evaluateCondition(
  condition: WorkflowCondition | null,
  facts: WorkflowFacts,
  trace: ConditionTrace[] = [],
  path = 'conditions',
  types?: WorkflowFieldTypes,
): boolean {
  if (!condition) return true
  if (condition.kind === 'group') {
    const results = condition.children.map((c, i) =>
      evaluateCondition(c, facts, trace, `${path}.children.${i}`, types),
    )
    // A group without a rule anywhere inside constrains nothing: it holds, negated or not (the
    // builder says so), and its parent ignores it, so "NOT (empty)" never silently matches nothing.
    const counted = results.filter((_, i) => conditionConstrains(condition.children[i] ?? null))
    const inner = condition.match === 'all' ? counted.every(Boolean) : counted.some(Boolean)
    const passed = counted.length === 0 ? true : condition.negate ? !inner : inner
    trace.push({ kind: 'group', path, passed, match: condition.match, negate: condition.negate })
    return passed
  }
  const actual = facts[condition.field] ?? null
  const type = types && Object.hasOwn(types, condition.field) ? types[condition.field] : undefined
  const passed =
    condition.operator === 'within' || condition.operator === 'notWithin'
      ? evaluateSubtreeRule(condition, facts)
      : type
        ? evaluateTypedRule(condition, actual, type)
        : evaluateRule(condition, actual)
  trace.push({
    kind: 'rule',
    path,
    passed,
    field: condition.field,
    operator: condition.operator,
    value: condition.value ?? null,
    actual,
  })
  return passed
}

/**
 * Whether a condition can exclude anything: it has a rule somewhere. Null and groups of (groups of)
 * nothing match every document, like no condition at all.
 */
export function conditionConstrains(condition: WorkflowCondition | null): boolean {
  if (!condition) return false
  return condition.kind === 'rule' || condition.children.some(conditionConstrains)
}

const MONEY_TEXT = /^-?\d{1,15}(\.\d{1,3})?$/

/**
 * within: the field's unit is the rule's unit or below it, read from the derived path fact (the unit and
 * its ancestors, captured at submission so a later move does not change a past run). A missing unit is
 * within nothing, so `notWithin` holds for it.
 */
function evaluateSubtreeRule(rule: WorkflowRule, facts: WorkflowFacts): boolean {
  const path = facts[orgPathFact(rule.field)] ?? null
  const inside = typeof rule.value === 'string' && Array.isArray(path) && path.some((id) => id === rule.value)
  return rule.operator === 'within' ? inside : !inside
}

function evaluateRule(rule: WorkflowRule, actual: string | string[] | null): boolean {
  const isEmpty = actual === null || (Array.isArray(actual) && actual.length === 0)
  switch (rule.operator) {
    case 'empty':
      return isEmpty
    case 'notEmpty':
      return !isEmpty
    case 'in':
    case 'notIn': {
      const wanted = new Set(Array.isArray(rule.value) ? rule.value : [])
      const have = Array.isArray(actual) ? actual : actual === null ? [] : [actual]
      const any = have.some((a) => wanted.has(a))
      return rule.operator === 'in' ? any : !any
    }
    default: {
      if (actual === null || Array.isArray(actual) || typeof rule.value !== 'string') {
        // "is not X" holds for an absent value; every other comparison needs one.
        return rule.operator === 'neq' && actual === null
      }
      // Money compares as exact decimals; references (uuids) compare as text. A uuid never looks
      // like an amount (it has dashes), so the two can never be confused.
      const numeric = MONEY_TEXT.test(actual) && MONEY_TEXT.test(rule.value)
      if (rule.operator === 'eq' || rule.operator === 'neq') {
        const same = numeric ? compareMoney(actual, rule.value) === 0 : actual === rule.value
        return rule.operator === 'eq' ? same : !same
      }
      if (!numeric) return false
      const c = compareMoney(actual, rule.value)
      if (rule.operator === 'gt') return c > 0
      if (rule.operator === 'gte') return c >= 0
      if (rule.operator === 'lt') return c < 0
      return c <= 0
    }
  }
}

/** Orders two values of a type (negative, zero, positive); null when the type has no order or a value is malformed. */
function compareTyped(type: WorkflowFieldType, a: string, b: string): number | null {
  switch (type) {
    case 'money':
    case 'number':
      return NUMBER_TEXT.test(a) && NUMBER_TEXT.test(b) ? compareMoney(a, b) : null
    case 'date':
      return isDateText(a) && isDateText(b) ? (a < b ? -1 : a > b ? 1 : 0) : null
    default:
      return null
  }
}

function evaluateTypedRule(
  rule: WorkflowRule,
  actual: string | string[] | null,
  type: WorkflowFieldType,
): boolean {
  const isEmpty = actual === null || (Array.isArray(actual) && actual.length === 0)
  // Text compares without letter case (condition values are lowercased when parsed).
  const norm = (s: string) => (type === 'text' ? s.toLowerCase() : s)
  switch (rule.operator) {
    case 'empty':
      return isEmpty
    case 'notEmpty':
      return !isEmpty
    case 'in':
    case 'notIn': {
      const wanted = new Set(Array.isArray(rule.value) ? rule.value : [])
      const have = Array.isArray(actual) ? actual : actual === null ? [] : [actual]
      const any = have.some((a) => wanted.has(norm(a)))
      return rule.operator === 'in' ? any : !any
    }
    default: {
      if (actual === null || Array.isArray(actual) || typeof rule.value !== 'string') {
        // "is not X" holds for an absent value; every other comparison needs one.
        return rule.operator === 'neq' && actual === null
      }
      const c = compareTyped(type, actual, rule.value)
      if (rule.operator === 'eq' || rule.operator === 'neq') {
        const same = c === null ? norm(actual) === rule.value : c === 0
        return rule.operator === 'eq' ? same : !same
      }
      if (c === null) return false
      if (rule.operator === 'gt') return c > 0
      if (rule.operator === 'gte') return c >= 0
      if (rule.operator === 'lt') return c < 0
      return c <= 0
    }
  }
}

// ── the spec of a version ────────────────────────────────────────────────────────

export const WORKFLOW_MAX_STEPS = 10
export const WORKFLOW_MAX_ASSIGNEES = 20

/** How far above the anchor unit a `HEAD_OF` assignee starts (0: the unit itself). */
export const WORKFLOW_HEAD_OF_MAX_UP = 5
/** How many levels an org-relative step may walk up past a vacant or excluded head. */
export const WORKFLOW_WALK_UP_MAX = 10
export const WORKFLOW_WALK_UP_DEFAULT = 3

/** Whose org unit an org-relative assignee starts from: the document's, or the requester's home unit. */
const orgAnchorSchema = z.enum(['subject', 'requester'])
/** subject: the document field holding the unit; requester: null (the home unit is used). */
const orgFieldSchema = z.string().min(1).max(40).nullable().default(null)

/**
 * Who approves a step: a named user, the members of a role, or someone found through the org chart
 * when the step starts. `HEAD_OF`: the head of the anchor unit or of its `up`-th ancestor; `HRBP_OF`:
 * the anchor unit's HR business partner. Org-relative assignees walk up past a vacant or excluded head
 * (up to the step's `walkUpLimit`) and fall back to the step's `fallbackRoleId`.
 */
export const workflowAssigneeSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('USER'), id: uuidSchema }).strict(),
  z.object({ type: z.literal('ROLE'), id: uuidSchema }).strict(),
  z
    .object({
      type: z.literal('HEAD_OF'),
      of: orgAnchorSchema,
      field: orgFieldSchema,
      up: z.number().int().min(0).max(WORKFLOW_HEAD_OF_MAX_UP).default(0),
    })
    .strict(),
  z.object({ type: z.literal('HRBP_OF'), of: orgAnchorSchema, field: orgFieldSchema }).strict(),
])
export type WorkflowAssignee = z.infer<typeof workflowAssigneeSchema>
export type WorkflowDirectAssignee = Extract<WorkflowAssignee, { type: 'USER' | 'ROLE' }>
export type WorkflowOrgAssignee = Extract<WorkflowAssignee, { type: 'HEAD_OF' | 'HRBP_OF' }>

/** Resolved through the org chart when the step starts (`HEAD_OF`, `HRBP_OF`). */
export function isOrgRelative(a: WorkflowAssignee): a is WorkflowOrgAssignee {
  return a.type === 'HEAD_OF' || a.type === 'HRBP_OF'
}

/** A named user or role. */
export function isDirectAssignee(a: WorkflowAssignee): a is WorkflowDirectAssignee {
  return a.type === 'USER' || a.type === 'ROLE'
}

/**
 * A stable identity for an assignee within a step (duplicates, list keys, diffs). Users and roles keep
 * their historical `TYPE:id` form.
 */
export function assigneeKey(a: WorkflowAssignee): string {
  switch (a.type) {
    case 'USER':
    case 'ROLE':
      return `${a.type}:${a.id}`
    case 'HEAD_OF':
      return `HEAD_OF:${a.of}:${a.field ?? ''}+${a.up}`
    case 'HRBP_OF':
      return `HRBP_OF:${a.of}:${a.field ?? ''}`
  }
}

/** The ref kind of a field that holds an org unit. */
const ORG_UNIT_REF: WorkflowRefKind = 'orgUnit'

// ── step timers ───────────────────────────────────────────────────────────────────

/** HOURS: exact elapsed time; WORKING_HOURS / WORKING_DAYS: on the company's working calendar. */
export const WORKFLOW_TIMER_UNITS = TIMER_UNITS
export const WORKFLOW_REMINDER_MAX_HOURS = 720
export const WORKFLOW_DUE_MAX_HOURS = 2160
export const WORKFLOW_TIMER_MAX_WORKING_DAYS = 90

/** A reminder or due time: `amount` `unit` after the step starts (`timerInstant` computes the instant). */
export interface WorkflowTimer {
  amount: number
  unit: TimerUnit
}

function timerSchemaUpTo(maxHours: number) {
  return z
    .object({ amount: z.number().int().min(1), unit: z.enum(WORKFLOW_TIMER_UNITS) })
    .strict()
    .superRefine((t, ctx) => {
      const max = t.unit === 'WORKING_DAYS' ? WORKFLOW_TIMER_MAX_WORKING_DAYS : maxHours
      if (t.amount > max) ctx.addIssue({ code: 'custom', path: ['amount'], message: `At most ${max}` })
    })
}

/** A due time: up to 2160 (working) hours or 90 working days. */
export const workflowTimerSchema = timerSchemaUpTo(WORKFLOW_DUE_MAX_HOURS)
/** A reminder: up to 720 (working) hours or 90 working days. */
const workflowReminderSchema = timerSchemaUpTo(WORKFLOW_REMINDER_MAX_HOURS)

/**
 * The step timers before units existed: whole calendar hours. Still accepted (stored versions are never
 * rewritten) and read as HOURS; a step may not give the same timer both ways.
 */
export interface LegacyStepTimers {
  reminderAfterHours?: number | null
  dueAfterHours?: number | null
}
const LEGACY_TIMERS = [
  ['reminderAfterHours', 'reminderAfter'],
  ['dueAfterHours', 'dueAfter'],
] as const

function liftLegacyTimers(raw: unknown, ctx: z.RefinementCtx): unknown {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return raw
  if (!LEGACY_TIMERS.some(([legacy]) => Object.hasOwn(raw, legacy))) return raw
  const out: Record<string, unknown> = { ...(raw as Record<string, unknown>) }
  for (const [legacy, key] of LEGACY_TIMERS) {
    if (!Object.hasOwn(out, legacy)) continue
    const hours = out[legacy]
    delete out[legacy]
    if (Object.hasOwn(out, key)) {
      ctx.addIssue({
        code: 'custom',
        path: [legacy],
        message: 'Give the time either in hours or with a unit',
      })
      continue
    }
    if (hours === undefined) continue
    out[key] = typeof hours === 'number' ? { amount: hours, unit: 'HOURS' } : hours
  }
  return out
}

const workflowStepObjectSchema = z
  .object({
    /** Stable within the workflow (survives reordering); lowercase letters, digits and dashes. */
    key: z.string().regex(/^[a-z0-9-]{1,40}$/, 'Invalid step key'),
    name: z.string().trim().min(1, 'Name the step').max(80),
    nameAr: optionalText(80),
    /** The step runs only when this holds (a conditional step); null = always. */
    when: workflowConditionSchema.nullable().default(null),
    /**
     * Specific users, existing roles and org-relative heads; the union is the step's potential
     * approvers. An org-relative assignee counts as one.
     */
    assignees: z.array(workflowAssigneeSchema).max(WORKFLOW_MAX_ASSIGNEES).default([]),
    /**
     * Org-relative steps: whose members decide when no eligible head is found up the tree. Optional so
     * stored specs keep their shape; required to publish a step with an org-relative assignee.
     */
    fallbackRoleId: uuidSchema.nullable().optional(),
    /** Org-relative steps: how many levels to walk up past a vacant or excluded head (default 3). */
    walkUpLimit: z.number().int().min(0).max(WORKFLOW_WALK_UP_MAX).optional(),
    /** ANY: one eligible approver completes the step. ALL: every resolved approver must approve. */
    completion: z.enum(['ANY', 'ALL']).default('ANY'),
    /** Separation of duties: someone who approved an earlier step of the run cannot act here. */
    excludePriorApprovers: z.boolean().default(true),
    /** After the step starts (a reminder counts from when each person got the task). */
    reminderAfter: workflowReminderSchema.nullable().default(null),
    dueAfter: workflowTimerSchema.nullable().default(null),
  })
  .strict()
/** What a step accepts: the timer fields, or the legacy hour fields (read as HOURS), never both. */
export type WorkflowStepInput = z.input<typeof workflowStepObjectSchema> & LegacyStepTimers

export const workflowStepSchema = z.preprocess<unknown, typeof workflowStepObjectSchema, WorkflowStepInput>(
  liftLegacyTimers,
  workflowStepObjectSchema,
)
export type WorkflowStep = z.output<typeof workflowStepObjectSchema>

/**
 * A version's configuration. Drafts may be incomplete (no steps yet); publishing checks
 * `specProblems` in full and the references (users, roles) on the server.
 */
export const workflowSpecSchema = z
  .object({
    /** Which submissions the workflow applies to; null = every submission of the subject. */
    conditions: workflowConditionSchema.nullable().default(null),
    steps: z.array(workflowStepSchema).max(WORKFLOW_MAX_STEPS).default([]),
    /** What happens once every step has approved. `none`: the document waits, approved, for a person to act. */
    onApproved: z
      .object({ action: z.string().max(40).nullable().default(null) })
      .strict()
      .default({ action: null }),
  })
  .strict()
export type WorkflowSpec = z.output<typeof workflowSpecSchema>
export type WorkflowSpecInput = z.input<typeof workflowSpecSchema>

/** Structural problems of a spec for its subject; `forPublish` adds what a draft may still lack. */
export function specProblems(
  subject: WorkflowSubject,
  spec: WorkflowSpec,
  forPublish: boolean,
): { path: string; message: string }[] {
  const out: { path: string; message: string }[] = []
  if (spec.conditions) out.push(...conditionProblems(subject, spec.conditions))
  const keys = new Set<string>()
  spec.steps.forEach((s, i) => {
    if (keys.has(s.key)) out.push({ path: `steps.${i}.key`, message: 'Two steps share a key' })
    keys.add(s.key)
    if (s.when) out.push(...conditionProblems(subject, s.when, `steps.${i}.when`))
    const seen = new Set<string>()
    s.assignees.forEach((a, j) => {
      const k = assigneeKey(a)
      if (seen.has(k)) out.push({ path: `steps.${i}.assignees.${j}`, message: 'Listed twice' })
      seen.add(k)
      if (!isOrgRelative(a)) return
      if (a.of === 'requester') {
        if (a.field !== null) {
          out.push({
            path: `steps.${i}.assignees.${j}.field`,
            message: 'The requester’s home unit is used: no field is needed',
          })
        }
        return
      }
      const def = a.field === null ? undefined : subjectField(subject, a.field)
      if (def?.type !== 'ref' || def.ref !== ORG_UNIT_REF) {
        out.push({
          path: `steps.${i}.assignees.${j}.field`,
          message: 'Choose a field that holds an org unit',
        })
      }
    })
    if (forPublish && s.assignees.length === 0) {
      out.push({ path: `steps.${i}.assignees`, message: 'Choose who approves this step' })
    }
    if (forPublish && s.assignees.some(isOrgRelative) && !s.fallbackRoleId) {
      out.push({
        path: `steps.${i}.fallbackRoleId`,
        message: 'Choose the role that decides when no head can be found',
      })
    }
    const remind = s.reminderAfter
    const due = s.dueAfter
    if (remind && due && remind.unit !== due.unit) {
      out.push({
        path: `steps.${i}.reminderAfter`,
        message: 'The reminder and the due time must use the same unit',
      })
    } else if (remind && due && remind.amount >= due.amount) {
      out.push({
        path: `steps.${i}.reminderAfter`,
        message: 'The reminder must come before the due time',
      })
    }
  })
  if (forPublish && spec.steps.length === 0)
    out.push({ path: 'steps', message: 'Add at least one approval step' })
  if (forPublish && spec.steps.length > 0 && spec.steps.every((s) => s.when !== null)) {
    out.push({
      path: 'steps',
      message: 'At least one step must always run, so a matching document can never pass with no approver',
    })
  }
  const action = spec.onApproved.action
  if (action !== null && !subjectAction(subject, action)) {
    out.push({ path: 'onApproved.action', message: 'This action is not available for the document type' })
  }
  return out
}

// ── definitions ──────────────────────────────────────────────────────────────────

export const workflowCreateSchema = z.object({
  name: z.string().trim().min(1, 'Name the workflow').max(120),
  nameAr: optionalText(120),
  description: optionalText(1000),
  subject: workflowSubjectSchema,
})
export type WorkflowCreate = z.input<typeof workflowCreateSchema>

export const workflowUpdateSchema = workflowCreateSchema
  .omit({ subject: true })
  .extend({ version: versionSchema })
export type WorkflowUpdate = z.input<typeof workflowUpdateSchema>

export const workflowDraftSchema = z.object({ version: versionSchema, spec: workflowSpecSchema })
export type WorkflowDraftInput = z.input<typeof workflowDraftSchema>

export const workflowVersionActionSchema = z.object({ version: versionSchema })
export const workflowPrioritySchema = z.object({
  version: versionSchema,
  /** Lower runs first. Unique per document type, so selection is never ambiguous. */
  priority: z.number().int().min(1).max(9999),
})

export const WORKFLOW_SORT_FIELDS = ['name', 'priority', 'updatedAt'] as const
export const workflowListQuerySchema = pageQuerySchema.extend({
  subject: workflowSubjectSchema.optional(),
  state: z.enum(['active', 'disabled', 'unpublished']).optional(),
})
export type WorkflowListQuery = z.input<typeof workflowListQuerySchema>

export interface WorkflowUserRef {
  id: string
  name: string
  nameAr: string | null
}

export interface WorkflowVersionRef {
  id: string
  number: number
  status: 'DRAFT' | 'PUBLISHED' | 'RETIRED'
  createdAt: string
  createdBy: WorkflowUserRef | null
  publishedAt: string | null
  publishedBy: WorkflowUserRef | null
}

export interface WorkflowListItem {
  id: string
  name: string
  nameAr: string | null
  subject: WorkflowSubject
  priority: number
  /** Active: enabled with a published version (new submissions are routed through it). */
  enabled: boolean
  published: WorkflowVersionRef | null
  draft: WorkflowVersionRef | null
  counts: { inProgress: number; blocked: number; failed: number }
  /**
   * Applicability of the published version (else the draft), for the list's scope sentence; `hasSpec`
   * false when neither exists. Optional in the type only until fixtures carry it.
   */
  scope?: { conditions: WorkflowCondition | null; hasSpec: boolean }
  updatedAt: string
  updatedBy: WorkflowUserRef | null
  version: number
}

export interface WorkflowDefinition extends WorkflowListItem {
  description: string | null
  publishedSpec: WorkflowSpec | null
  draftSpec: WorkflowSpec | null
  createdAt: string
  createdBy: WorkflowUserRef | null
}

export interface WorkflowVersion extends WorkflowVersionRef {
  spec: WorkflowSpec
  runCount: number
}

/** Who can be named in a step (active users and existing roles), for holders of workflows.edit. */
export interface WorkflowAssignables {
  users: (WorkflowUserRef & { email: string; roleIds: string[] })[]
  roles: { id: string; name: string; nameAr: string | null; memberCount: number }[]
}

/** What publishing would do and what is wrong, before the publisher confirms. */
/** The publish check's codes: the UI translates by code (with `params`), `message` is the English fallback. */
export const WORKFLOW_CHECK_CODES = [
  'MISSING_ASSIGNEE',
  'MISSING_FALLBACK_ROLE',
  'ORG_RELATIVE_STEP',
  'FALLBACK_NO_ONE',
  'NO_ONE_CAN_DECIDE',
  'ONLY_ONE_CAN_DECIDE',
  'NO_HOLIDAYS',
  'SHADOWED',
] as const
export type WorkflowCheckCode = (typeof WORKFLOW_CHECK_CODES)[number]

export interface WorkflowCheckItem {
  path: string
  /** English, for clients that do not know the code. */
  message: string
  code?: WorkflowCheckCode
  /**
   * Values for the translated text: `step` (step name), `name` (a person or workflow), `order`,
   * `year`, `subject` (a WorkflowSubject) and `permission` (a Permission key).
   */
  params?: Record<string, string | number>
}

export interface WorkflowPublishCheck {
  problems: WorkflowCheckItem[]
  /** Non-blocking: a step no one can currently act on, a role with fewer than two members, … */
  warnings: WorkflowCheckItem[]
  /** Runs still on earlier versions: they finish on the version they started with. */
  runsOnEarlierVersions: number
}

// ── simulation ───────────────────────────────────────────────────────────────────

export const workflowSimulateSchema = z
  .object({
    subject: workflowSubjectSchema,
    /**
     * LIVE: the selection real submissions get (enabled, published workflows by priority).
     * DRAFT / PUBLISHED: one workflow's draft or published version, as if it were the only one.
     */
    target: z.enum(['LIVE', 'DRAFT', 'PUBLISHED']).default('LIVE'),
    workflowId: uuidSchema.optional(),
    /** A real document the caller may read; its facts and requester are used. */
    documentId: uuidSchema.optional(),
    /** Clearly synthetic inputs instead of a document. */
    /** Field values only: derived facts (dotted keys such as `orgUnit.path`) are computed by the server. */
    facts: z
      .record(
        z.string().refine((k) => !k.includes('.'), 'Derived values are computed, not supplied'),
        z.union([z.string(), z.array(z.string()), z.null()]),
      )
      .optional(),
    requesterId: uuidSchema.optional(),
  })
  .refine((v) => v.target === 'LIVE' || v.workflowId, {
    path: ['workflowId'],
    message: 'Choose the workflow to test',
  })
  .refine((v) => Boolean(v.documentId) !== Boolean(v.facts), {
    path: ['documentId'],
    message: 'Test with either a document or sample values',
  })
export type WorkflowSimulateInput = z.input<typeof workflowSimulateSchema>

export type CandidateExclusion =
  | 'REQUESTER'
  | 'USER_INACTIVE'
  | 'MISSING_PERMISSION'
  | 'PRIOR_APPROVER'
  | 'NOT_ASSIGNED'
  /** The module owning the document is off for the company: nobody can decide until it is back. */
  | 'SUBJECT_MODULE_OFF'

/**
 * One level an org-relative assignee passed on its way up (a vacant or excluded head), or the
 * fallback role taking over when nobody was found.
 */
export interface WorkflowResolutionWalk {
  /** `assigneeKey` of the assignee that walked. */
  assignee: string
  /** The unit whose head was considered (null: the anchor unit is unknown). */
  unitId: string | null
  /** Who was found there, if anyone. */
  user: WorkflowUserRef | null
  reason: CandidateExclusion | 'VACANT' | 'NO_UNIT' | 'FALLBACK'
}

export interface ResolvedCandidates {
  eligible: (WorkflowUserRef & { via: string[] })[]
  excluded: (WorkflowUserRef & { reason: CandidateExclusion; via: string[] })[]
  /** Configured users or roles that no longer exist. */
  missing: { type: 'USER' | 'ROLE'; id: string }[]
  /** Org-relative assignees: each level walked past and why (absent for user and role steps). */
  walks?: WorkflowResolutionWalk[]
}

export interface SimulatedStep {
  key: string
  name: string
  nameAr: string | null
  applies: boolean
  when: ConditionTrace[]
  completion: 'ANY' | 'ALL'
  candidates: ResolvedCandidates | null
  /** The step would stop the run: no eligible approver. */
  blocked: boolean
  reminderAfter: WorkflowTimer | null
  dueAfter: WorkflowTimer | null
  /** When the reminder and due time would fall were the step to start now (ISO; on the working calendar). */
  reminderAt?: string | null
  dueAt?: string | null
}

export interface WorkflowSimulation {
  /** Always true: nothing was changed, sent or started. */
  readOnly: true
  synthetic: boolean
  facts: WorkflowFacts
  requester: WorkflowUserRef | null
  document: { id: string; label: string } | null
  /** Every workflow considered, in selection order, with its condition result. */
  considered: {
    workflowId: string
    name: string
    nameAr: string | null
    versionNumber: number
    source: 'DRAFT' | 'PUBLISHED'
    priority: number
    matched: boolean
    selected: boolean
    trace: ConditionTrace[]
  }[]
  selected: { workflowId: string; name: string; nameAr: string | null; versionNumber: number } | null
  steps: SimulatedStep[]
  outcome: 'NO_APPROVAL_NEEDED' | 'WOULD_WAIT_FOR_APPROVAL' | 'WOULD_BLOCK' | 'ALREADY_PENDING'
  onApproved: { action: string | null; label: { en: string; ar: string } | null }
}

// ── runs, steps, tasks ───────────────────────────────────────────────────────────

export const WORKFLOW_RUN_STATUSES = [
  'IN_PROGRESS',
  'BLOCKED',
  'ACTION_PENDING',
  'ACTION_FAILED',
  'APPROVED',
  'REJECTED',
  'RETURNED',
  'CANCELLED',
  'SUPERSEDED',
] as const
export type WorkflowRunStatus = (typeof WORKFLOW_RUN_STATUSES)[number]
/** Statuses in which the document is held by the run (locked for edits and posting). */
export const WORKFLOW_ACTIVE_STATUSES = ['IN_PROGRESS', 'BLOCKED', 'ACTION_PENDING', 'ACTION_FAILED'] as const

export type WorkflowStepStatus =
  | 'PENDING'
  | 'ACTIVE'
  | 'APPROVED'
  | 'SKIPPED'
  | 'REJECTED'
  | 'RETURNED'
  | 'CANCELLED'
export type WorkflowTaskStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'RETURNED' | 'CLOSED' | 'REASSIGNED'

export type WorkflowBlockReason = 'NO_ELIGIBLE_APPROVER' | 'NO_STEP_APPLIES'

export type WorkflowEventType =
  | 'SUBMITTED'
  | 'STEP_ACTIVATED'
  | 'STEP_SKIPPED'
  | 'STEP_COMPLETED'
  | 'TASK_APPROVED'
  | 'TASK_REJECTED'
  | 'TASK_RETURNED'
  | 'BLOCKED'
  | 'APPROVERS_REFRESHED'
  | 'REASSIGNED'
  | 'REMINDER_DUE'
  | 'OVERDUE'
  | 'APPROVED'
  | 'ACTION_QUEUED'
  | 'ACTION_SUCCEEDED'
  | 'ACTION_FAILED'
  | 'ACTION_RETRIED'
  | 'DOCUMENT_ACTED'
  | 'CANCELLED'
  | 'SUPERSEDED'

export interface WorkflowEvent {
  id: string
  type: WorkflowEventType
  at: string
  actor: WorkflowUserRef | null
  /** Set when the actor acted for someone else through a delegation. */
  onBehalfOf: WorkflowUserRef | null
  stepIndex: number | null
  comment: string | null
  /** Structured detail (reason codes, counts, error code); never secrets. */
  data: Record<string, unknown>
}

export interface WorkflowTask {
  id: string
  stepIndex: number
  assignee: WorkflowUserRef
  status: WorkflowTaskStatus
  decidedBy: WorkflowUserRef | null
  /** The decider acted through a delegation from the assignee. */
  delegated: boolean
  decidedAt: string | null
  comment: string | null
  dueAt: string | null
  overdue: boolean
  /** Live check for pending tasks: may the assignee still decide right now? */
  eligibleNow: boolean | null
  ineligibleReason: CandidateExclusion | null
  /**
   * How the assignee was found (WF4), e.g. `HEAD_OF:subject+1` with the walk and any skip reason; null for
   * USER/ROLE assignees and manual reassignments.
   */
  resolvedVia: string | null
}

export interface WorkflowRunStep {
  index: number
  key: string
  name: string
  nameAr: string | null
  completion: 'ANY' | 'ALL'
  status: WorkflowStepStatus
  activatedAt: string | null
  completedAt: string | null
  dueAt: string | null
  /** Resolved when the step activated (who was eligible and why others were not). */
  candidates: ResolvedCandidates | null
  tasks: WorkflowTask[]
}

/** The document a run is about, as its module describes it (amount only for readers of the document). */
export interface WorkflowSubjectSummary {
  subject: WorkflowSubject
  id: string
  label: string
  reference: string | null
  party: string | null
  amount: string | null
  /** The document changed after this run was bound to it. */
  changedSinceSubmission: boolean
  lines: { description: string; quantity: string; amount: string | null }[]
  /**
   * `project_budget`: the project the budget belongs to, so approvals link to its Budget & cost tab.
   * Absent for other subjects.
   */
  projectId?: string | null
  /**
   * `project_budget`: why this version was made and how much it changes the approved budget (signed;
   * the whole cost for a first budget), shown to the approver. Absent for other subjects.
   */
  budget?: { versionNo: number; reason: string | null; changeVsBaseline: string } | null
  /**
   * `service_contract`: the contract's period, sites and coverage in short, shown to the approver (no money
   * beyond `amount`). Absent for other subjects.
   */
  serviceContract?: {
    startDate: string
    endDate: string
    siteCount: number
    coveredAssets: number
    renews: string | null
  } | null
}

export interface WorkflowRunListItem {
  id: string
  subject: WorkflowSubject
  subjectId: string
  subjectLabel: string
  amount: string | null
  workflow: { id: string; name: string; nameAr: string | null; versionNumber: number }
  status: WorkflowRunStatus
  blockedReason: WorkflowBlockReason | null
  currentStep: { index: number; name: string; nameAr: string | null; total: number } | null
  requester: WorkflowUserRef | null
  submittedAt: string
  completedAt: string | null
  /** When the current wait began (step activation or last change). */
  waitingSince: string | null
  lastEvent: { type: WorkflowEventType; at: string } | null
  overdue: boolean
  version: number
}

export interface WorkflowRun extends WorkflowRunListItem {
  revision: number
  facts: WorkflowFacts
  previousRunId: string | null
  nextRunId: string | null
  steps: WorkflowRunStep[]
  events: WorkflowEvent[]
  action: {
    action: string
    status: 'QUEUED' | 'SUCCEEDED' | 'FAILED'
    attempts: number
    errorCode: string | null
    errorMessage: string | null
    updatedAt: string
  } | null
  document: WorkflowSubjectSummary | null
  /** The action after approval of the version the run is pinned to (e.g. `post`); null: none. */
  afterApproval: string | null
  /** What the viewer may do now. */
  can: { reassign: boolean; refresh: boolean; retry: boolean; cancel: boolean; withdraw: boolean }
}

export const WORKFLOW_RUN_SORT_FIELDS = ['submittedAt', 'status', 'waitingSince'] as const
export const workflowRunListQuerySchema = pageQuerySchema.extend({
  status: z.enum(WORKFLOW_RUN_STATUSES).optional(),
  /** active = every status that still holds its document. */
  state: z.enum(['active', 'attention', 'closed']).optional(),
  subject: workflowSubjectSchema.optional(),
  workflowId: uuidSchema.optional(),
  subjectId: uuidSchema.optional(),
})
export type WorkflowRunListQuery = z.input<typeof workflowRunListQuerySchema>

/**
 * Who an administrator may hand the active step to (`GET /workflow-runs/:id/reassign-candidates`):
 * every active user with the reason they cannot take it, or null when they can.
 */
export interface ReassignCandidate extends WorkflowUserRef {
  reason: CandidateExclusion | 'YOURSELF' | 'DELEGATES_TO_YOU' | 'ALREADY_IN_STEP' | null
}

export const reassignTaskSchema = z.object({
  version: versionSchema,
  toUserId: uuidSchema,
  reason: z.string().trim().min(3, 'Give a reason').max(500),
})
export const runRecoverySchema = z.object({ version: versionSchema })
export const runCancelSchema = z.object({
  version: versionSchema,
  reason: z.string().trim().min(3, 'Give a reason').max(500),
})

// ── my approvals (the workspace inbox) ───────────────────────────────────────────

export const approvalTaskListQuerySchema = pageQuerySchema.extend({
  view: z.enum(['pending', 'completed']).default('pending'),
  subject: workflowSubjectSchema.optional(),
})
export type ApprovalTaskListQuery = z.input<typeof approvalTaskListQuerySchema>

export const submittedRunListQuerySchema = pageQuerySchema.extend({
  state: z.enum(['active', 'closed']).optional(),
})

export interface ApprovalTaskItem {
  taskId: string
  runId: string
  subject: WorkflowSubject
  subjectId: string
  subjectLabel: string
  /** Null when the viewer may not read the document's amounts. */
  amount: string | null
  requester: WorkflowUserRef | null
  workflowName: string
  workflowNameAr: string | null
  step: { index: number; name: string; nameAr: string | null; total: number; completion: 'ANY' | 'ALL' }
  status: WorkflowTaskStatus
  runStatus: WorkflowRunStatus
  submittedAt: string
  activatedAt: string
  dueAt: string | null
  overdue: boolean
  /** The task is someone else's; the viewer holds it through a delegation. */
  onBehalfOf: WorkflowUserRef | null
  decidedAt: string | null
  decision: 'APPROVED' | 'REJECTED' | 'RETURNED' | null
}

export interface ApprovalCounts {
  pending: number
  overdue: number
}

export const approvalDecisionSchema = z
  .object({
    decision: z.enum(['APPROVE', 'REJECT', 'RETURN']),
    comment: optionalText(1000),
  })
  .refine((v) => v.decision === 'APPROVE' || (v.comment && v.comment.length >= 3), {
    path: ['comment'],
    message: 'Give a reason',
  })
export type ApprovalDecisionInput = z.input<typeof approvalDecisionSchema>

// ── delegation ───────────────────────────────────────────────────────────────────

export const delegationInputSchema = z
  .object({
    /** Omitted: the caller delegates their own approvals. Another user needs workflows.delegations. */
    fromUserId: uuidSchema.optional(),
    toUserId: uuidSchema,
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }),
    /** Null: every document type. */
    subject: workflowSubjectSchema.nullable().default(null),
    reason: z.string().trim().min(3, 'Give a reason').max(500),
  })
  // Instants, not text: "10:00+04:00" is before "08:00Z".
  .refine((v) => Date.parse(v.endsAt) > Date.parse(v.startsAt), {
    path: ['endsAt'],
    message: 'The end must be after the start',
  })
export type DelegationInput = z.input<typeof delegationInputSchema>

export const delegationListQuerySchema = pageQuerySchema.extend({
  state: z.enum(['current', 'upcoming', 'past']).optional(),
  userId: uuidSchema.optional(),
})

export interface Delegation {
  id: string
  from: WorkflowUserRef
  to: WorkflowUserRef
  subject: WorkflowSubject | null
  startsAt: string
  endsAt: string
  reason: string
  state: 'current' | 'upcoming' | 'past' | 'revoked'
  createdAt: string
  createdBy: WorkflowUserRef | null
  revokedAt: string | null
  revokedBy: WorkflowUserRef | null
}

// ── a document's approval state (embedded in the document's DTO) ─────────────────

export interface DocumentApproval {
  /** A published, enabled workflow applies to the document as it stands now. */
  required: boolean
  workflow: { id: string; name: string; nameAr: string | null } | null
  /** The latest run for this document, if any. */
  run: {
    id: string
    status: WorkflowRunStatus
    blockedReason: WorkflowBlockReason | null
    currentStep: { index: number; name: string; nameAr: string | null; total: number } | null
    submittedAt: string
    submittedBy: WorkflowUserRef | null
    completedAt: string | null
    lastComment: string | null
    /** The run was bound to the document's current content (false after a material change). */
    matchesDocument: boolean
    /** The viewer holds a pending task (or a delegated one) on it. */
    myTaskId: string | null
    version: number
  } | null
  /** Submit, then post only with an approval that matches; posting is otherwise refused. */
  canPostNow: boolean
  /**
   * The company's approval workflows module (or the module owning the document, ADR-024) is off and
   * an open request holds the document: the request is frozen (nobody can decide, withdraw or recover
   * it) and the document stays locked until the module is turned back on.
   */
  onHold: boolean
}

export const WORKFLOW_ERROR_CODES = [
  'APPROVAL_REQUIRED',
  'APPROVAL_PENDING',
  'APPROVAL_ON_HOLD',
  'APPROVAL_STALE',
  'APPROVAL_REJECTED_UNCHANGED',
  'NO_APPROVAL_NEEDED',
  'TASK_CLOSED',
  'RUN_NOT_ACTIVE',
  'NOT_ELIGIBLE',
  'SELF_APPROVAL',
  'ALREADY_DECIDED_IN_STEP',
  'WORKFLOW_INVALID',
  'WORKFLOW_PRIORITY_TAKEN',
  'WORKFLOW_ACTION_NOT_ALLOWED',
  'WORKFLOW_NOT_PUBLISHED',
  'DELEGATION_INVALID',
] as const
